// Drawer bar component with toggle buttons

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import './toolbar-button.js';
import './toolbar-well.js';

/**
 * @typedef {'usersList' | 'monsterLibrary' | 'info' | 'characterSheet' | 'effects' | 'history' | 'decorations'} DrawerView
 */

/**
 * Drawer bar component
 * Provides buttons to toggle drawer views
 */
class DrawerBar extends BaseComponent {
  static properties = {
    drawerOpen: { type: Boolean, attribute: 'drawer-open' },
    activeView: { type: String, attribute: 'active-view' },
    infoBadge: { type: Boolean, attribute: 'info-badge' },
    showUsers: { type: Boolean, attribute: 'show-users' },
    showCharacterSheet: { type: Boolean, attribute: 'show-character-sheet' },
    showMonsterLibrary: { type: Boolean, attribute: 'show-monster-library' },
    showInfo: { type: Boolean, attribute: 'show-info' },
    showEffects: { type: Boolean, attribute: 'show-effects' },
    showHistory: { type: Boolean, attribute: 'show-history' },
    showDecorations: { type: Boolean, attribute: 'show-decorations' },
  };

  static styles = css`
    :host {
      display: block;
      position: absolute;
      top: 20px;
      right: 20px;
      z-index: 200;
    }

    .drawer-bar {
    }

    /* .tool-separator styling lives on toolbar-well via ::slotted so the
       rule is direction-aware (column vs row). Icon sizing lives on
       toolbar-button via ::slotted(svg). */
  `;

  constructor() {
    super();
    this.drawerOpen = false;
    /** @type {DrawerView | null} */
    this.activeView = null;
    this.infoBadge = false;
    this.showUsers = false;
    this.showCharacterSheet = false;
    this.showMonsterLibrary = false;
    this.showInfo = false;
    this.showEffects = false;
    this.showHistory = false;
    this.showDecorations = false;
  }

  _handleCloseDrawer() {
    this.dispatchEvent(new CustomEvent('close-drawer', { bubbles: true, composed: true }));
  }

  /** @param {DrawerView} view */
  _handleToggleView(view) {
    this.dispatchEvent(new CustomEvent('toggle-view', { detail: view, bubbles: true, composed: true }));
  }

  /** @param {string} name */
  _renderIcon(name) {
    /** @type {Record<string, import('lit').TemplateResult>} */
    const icons = {
      'panel-right-close': html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="15" y1="3" x2="15" y2="21" />
          <polyline points="9 15 6 12 9 9" />
        </svg>
      `,
      users: html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 21a8 8 0 0 0-16 0" />
          <circle cx="10" cy="8" r="5" />
          <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
        </svg>
      `,
      'user-square': html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="12" cy="10" r="3" />
          <path d="M7 21v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
        </svg>
      `,
      ghost: html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M9 10h.01" />
          <path d="M15 10h.01" />
          <path d="M12 2a8 8 0 0 0-8 8v12l3-3 2.5 2.5L12 19l2.5 2.5L17 19l3 3V10a8 8 0 0 0-8-8z" />
        </svg>
      `,
      info: html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      `,
      sparkles: html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
          />
          <path d="M5 3v4" />
          <path d="M19 17v4" />
          <path d="M3 5h4" />
          <path d="M17 19h4" />
        </svg>
      `,
      'clock-history': html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l3 2" />
        </svg>
      `,
      armchair: html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 9V6a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v3" />
          <path d="M3 11a3 3 0 0 1 6 0v3h6v-3a3 3 0 1 1 6 0v5a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3z" />
          <path d="M5 18v3" />
          <path d="M19 18v3" />
        </svg>
      `,
    };
    return icons[name] || html``;
  }

  render() {
    return html`
      <div class="drawer-bar">
        <toolbar-well>
          <toolbar-button tooltip="Close Drawer" tooltip-position="left" @click="${this._handleCloseDrawer}">
            ${this._renderIcon('panel-right-close')}
          </toolbar-button>

          <div class="tool-separator"></div>

          ${
            this.showUsers
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'usersList'}"
                  tooltip="Users"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('usersList')}"
                >
                  ${this._renderIcon('users')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showCharacterSheet
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'characterSheet'}"
                  tooltip="Character Sheet"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('characterSheet')}"
                >
                  ${this._renderIcon('user-square')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showMonsterLibrary
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'monsterLibrary'}"
                  tooltip="Monster Library"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('monsterLibrary')}"
                >
                  ${this._renderIcon('ghost')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showEffects
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'effects'}"
                  tooltip="Effects"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('effects')}"
                >
                  ${this._renderIcon('sparkles')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showInfo
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'info'}"
                  ?badge="${this.infoBadge}"
                  tooltip="Info"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('info')}"
                >
                  ${this._renderIcon('info')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showHistory
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'history'}"
                  tooltip="History"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('history')}"
                >
                  ${this._renderIcon('clock-history')}
                </toolbar-button>
              `
              : ''
          }
          ${
            this.showDecorations
              ? html`
                <toolbar-button
                  ?active="${this.drawerOpen && this.activeView === 'decorations'}"
                  tooltip="Decorations"
                  tooltip-position="left"
                  @click="${() => this._handleToggleView('decorations')}"
                >
                  ${this._renderIcon('armchair')}
                </toolbar-button>
              `
              : ''
          }
        </toolbar-well>
      </div>
    `;
  }
}

customElements.define('drawer-bar', DrawerBar);
